import { FilePlusIcon } from '@radix-ui/react-icons'
import { useRuntimeVariables } from 'app-kit'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useGeckoAnalytics } from 'features/analytics'
import { useKestrel } from 'features/kestrelViewer'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { ViewToggle } from 'features/kestrelViewer/ui/tools/ViewToggle'
import { convertAnnotationsToPDF } from 'features/kestrelViewer/utils/pdfExportUtils'
import React, { useState } from 'react'

// Simple CSS spinner component
const Spinner: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <div
    style={{
      width: size,
      height: size,
      border: '2px solid #f3f3f3',
      borderTop: '2px solid #333',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }}
  />
)

// Add CSS animation for spinner (this could also be moved to a CSS file)
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

export const DownloadIndicationsPDF: React.FC = () => {
  const { trackButtonClicked } = useGeckoAnalytics()
  const { annotations, kestrelModel, authorizedAxios } = useKestrel()
  const { photos } = useKestrelViewerContext()
  const { KESTREL_API_URL } = useRuntimeVariables()
  const [isGenerating, setIsGenerating] = useState(false)

  const handleDownload = async () => {
    if (isGenerating) return // Prevent multiple clicks

    setIsGenerating(true)

    try {
      // Extract component metadata from kestrelModel tags
      const metadata = {
        organizationName: kestrelModel?.tags.organization,
        siteName: kestrelModel?.tags.site,
        componentName: kestrelModel?.tags.component,
      }

      // Convert annotations to PDF with enhanced photo integration
      const pdfBlob = await convertAnnotationsToPDF(
        annotations,
        metadata,
        photos,
        kestrelModel?.id,
        authorizedAxios,
        KESTREL_API_URL
      )

      const url = URL.createObjectURL(pdfBlob)

      const link = document.createElement('a')
      link.href = url
      link.download = 'defects-report.pdf'
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      trackButtonClicked({
        button_name: 'kestrel_download_pdf',
        kestrel_model_id: kestrelModel?.data.kestrel_model_id,
      })
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      // You might want to show a toast notification here
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <>
      <style>{spinnerStyles}</style>
      <div
        style={{
          opacity: isGenerating ? 0.6 : 1,
          pointerEvents: isGenerating ? 'none' : 'auto',
          cursor: isGenerating ? 'not-allowed' : 'pointer',
        }}>
        <TooltipWrapper
          side={'top'}
          offset={5}
          label={
            isGenerating ? 'Generating PDF...' : 'Download Defects Report PDF'
          }>
          <ViewToggle
            onClick={() => {
              if (!isGenerating) {
                handleDownload()
              }
            }}>
            {isGenerating ? (
              <Spinner size={22} />
            ) : (
              <FilePlusIcon width={22} height={22} />
            )}
          </ViewToggle>
        </TooltipWrapper>
      </div>
    </>
  )
}
